import DOMPurify from "dompurify";
import { FileText, Inbox, Send, Sparkles } from "lucide-react";
import { useState } from "react";

import { ConvoSplitView } from "@/components/admin/agenti/ConvoSplitView";
import { ThreadExportButtons } from "@/components/admin/agenti/ThreadExportButtons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type Allegato,
  type StatoEmail,
  useAgenteEmail,
  useAgenteEmailThread,
} from "@/hooks/useAgenteEmail";
import { apiDownloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";

const FILTRI: { key: string; label: string }[] = [
  { key: "tutte", label: "Tutte" },
  { key: "inviata", label: "Inviate" },
  { key: "errore", label: "In errore" },
  { key: "bloccata", label: "Bloccate" },
  { key: "trattenuta", label: "Trattenute (test)" },
];

const TAG: Record<StatoEmail, { label: string; cls: string }> = {
  inviata: { label: "inviata", cls: "bg-emerald-50 text-emerald-700" },
  errore: { label: "errore", cls: "bg-red-50 text-red-700" },
  bloccata: { label: "bloccata", cls: "bg-orange-50 text-orange-700" },
  trattenuta: { label: "trattenuta", cls: "bg-amber-100 text-amber-800" },
  in_corso: { label: "in corso", cls: "bg-blue-50 text-blue-700" },
};

const INVIO: Record<string, { label: string; cls: string }> = {
  inviata: { label: "Inviata", cls: "bg-emerald-50 text-emerald-700" },
  composta: { label: "Spedita", cls: "bg-slate-100 text-slate-600" },
  errore: { label: "Errore invio", cls: "bg-red-50 text-red-700" },
  bloccata: { label: "Bloccata", cls: "bg-orange-50 text-orange-700" },
  trattenuta: { label: "Trattenuta (test)", cls: "bg-amber-100 text-amber-800" },
};

function AllegatoChip({ slug, a }: { slug: string; a: Allegato }) {
  return (
    <button
      type="button"
      title="Scarica per ispezionare"
      onClick={() =>
        apiDownloadFile(`/api/v1/admin/agenti/${slug}/files/${a.id}/download`, a.nome)
      }
      className="inline-flex items-center gap-1.5 rounded-md border bg-muted px-2 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-card hover:border-brand-700"
    >
      <FileText className="size-3 text-red-500" /> {a.nome}
    </button>
  );
}

function HtmlBody({ html }: { html: string | null }) {
  if (!html) return null;
  return (
    <div
      className="mt-2 text-sm leading-relaxed [&_a]:text-brand-700 [&_a]:underline [&_p]:my-1 [&_table]:my-2 [&_td]:px-1.5 [&_td]:py-0.5 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
      // Contenuto generato da Aida, sanitizzato: mostriamo il risultato, non il codice.
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

export function CanaleEmail({ slug }: { slug: string }) {
  const [filtro, setFiltro] = useState("tutte");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const lista = useAgenteEmail(slug, { stato: filtro, q: q || undefined });
  const thread = useAgenteEmailThread(slug, sel);

  const righe = lista.data?.email ?? [];

  const listaNode = (
    <>
      <div className="space-y-2 border-b bg-card p-2.5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca nelle email…"
          className="w-full rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-700/30"
        />
        <div className="flex flex-wrap gap-1.5">
          {FILTRI.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFiltro(f.key)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11.5px] font-medium",
                filtro === f.key
                  ? "border-brand-700 bg-brand-50 text-brand-700"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {lista.isLoading ? (
        <div className="p-3">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : righe.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Nessuna email per questo filtro.</p>
      ) : (
        righe.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setSel(r.id)}
            className={cn(
              "block w-full border-b px-3 py-2.5 text-left hover:bg-card",
              sel === r.id && "border-l-[3px] border-l-brand-700 bg-brand-50",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[13px] font-semibold">{r.cliente}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {r.ultima_attivita_il
                  ? new Date(r.ultima_attivita_il).toLocaleDateString("it-IT")
                  : ""}
              </span>
            </div>
            <div className="truncate text-xs text-muted-foreground">{r.oggetto || "—"}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold", TAG[r.stato].cls)}>
                {TAG[r.stato].label}
              </span>
              {r.paese_iso && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                  {r.paese_iso}
                </span>
              )}
              {r.n_allegati > 0 && (
                <span className="text-[10px] text-muted-foreground">{r.n_allegati} allegati</span>
              )}
            </div>
          </button>
        ))
      )}
    </>
  );

  let dettaglioNode: React.ReactNode;
  if (!sel) {
    dettaglioNode = (
      <div className="grid flex-1 place-items-center p-8 text-sm text-muted-foreground">
        Seleziona un'email per vedere la timeline completa.
      </div>
    );
  } else if (thread.isLoading) {
    dettaglioNode = (
      <div className="p-4">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  } else if (thread.data) {
    const d = thread.data;
    dettaglioNode = (
      <>
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">
              {d.cliente}
              {d.riferimento ? ` · ${d.riferimento}` : ""}
            </div>
            <div className="text-xs text-muted-foreground">
              {d.tipo === "carico" ? "Thread del carico" : "Email non agganciata a un carico"}
            </div>
          </div>
          <div className="ml-auto">
            <ThreadExportButtons slug={slug} canale="email" threadId={sel} />
          </div>
        </div>
        <div className="max-h-[600px] space-y-3 overflow-y-auto p-4">
          {d.motivo && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              {d.motivo}
            </div>
          )}
          {d.eventi.map((ev, i) => {
            const quando = new Date(ev.quando).toLocaleString("it-IT");
            if (ev.tipo === "ricevuta") {
              return (
                <Stadio key={i} icona={<Inbox />} titolo="Email ricevuta" colore="emerald" quando={quando}>
                  <Meta label="Da" valore={ev.mittente} />
                  <Meta label="Oggetto" valore={ev.oggetto} />
                  {ev.corpo && <p className="mt-2 whitespace-pre-wrap text-sm">{ev.corpo}</p>}
                  <Allegati slug={slug} items={ev.allegati} etichetta="Allegati ricevuti" />
                </Stadio>
              );
            }
            if (ev.tipo === "operazioni") {
              return (
                <Stadio key={i} icona={<Sparkles />} titolo="Cosa ha fatto Aida" colore="blue" quando={quando}>
                  <ul className="space-y-1.5">
                    {ev.voci.map((v, j) => (
                      <li key={j} className="text-[13px]">
                        {v.dettaglio ? (
                          <details className="group">
                            <summary className="cursor-pointer list-none">
                              <span className="font-medium">{v.titolo}</span>
                              <span className="ml-1 text-[11px] text-brand-700 group-open:hidden">
                                — dettaglio
                              </span>
                            </summary>
                            <div className="mt-1 whitespace-pre-wrap rounded-md bg-muted/50 px-2 py-1.5 text-xs text-muted-foreground">
                              {v.dettaglio}
                            </div>
                          </details>
                        ) : (
                          <span className="font-medium">{v.titolo}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </Stadio>
              );
            }
            // inviata
            const badge = INVIO[ev.stato_invio] ?? INVIO.composta;
            const recapitato = ev.stato_invio === "inviata" || ev.stato_invio === "composta";
            return (
              <Stadio
                key={i}
                icona={<Send />}
                titolo="Risposta inviata"
                colore={ev.stato_invio === "errore" || ev.stato_invio === "bloccata" ? "red" : "emerald"}
                quando={quando}
                badge={badge}
              >
                <Meta label="A" valore={ev.destinatari.join(", ")} />
                <Meta label="Oggetto" valore={ev.oggetto} />
                {ev.errore_invio && <Meta label="Errore" valore={ev.errore_invio} />}
                <HtmlBody html={ev.corpo} />
                <Allegati
                  slug={slug}
                  items={ev.allegati}
                  etichetta={recapitato ? "Allegati inviati" : "Allegati preparati (non recapitati)"}
                />
              </Stadio>
            );
          })}
        </div>
      </>
    );
  } else {
    dettaglioNode = (
      <div className="p-4 text-sm text-muted-foreground">Impossibile caricare il thread.</div>
    );
  }

  return <ConvoSplitView lista={listaNode} dettaglio={dettaglioNode} />;
}

const COLORE: Record<string, string> = {
  emerald: "bg-emerald-50 text-emerald-600",
  blue: "bg-blue-50 text-blue-600",
  red: "bg-red-50 text-red-600",
};

function Stadio({
  icona,
  titolo,
  colore,
  quando,
  badge,
  children,
}: {
  icona: React.ReactNode;
  titolo: string;
  colore: string;
  quando: string;
  badge?: { label: string; cls: string };
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className={cn("grid size-8 shrink-0 place-items-center rounded-lg [&_svg]:size-4", COLORE[colore])}>
        {icona}
      </div>
      <div className="min-w-0 flex-1 rounded-lg border bg-card p-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold">{titolo}</span>
          {badge && (
            <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold", badge.cls)}>
              {badge.label}
            </span>
          )}
          <span className="ml-auto text-[11px] text-muted-foreground">{quando}</span>
        </div>
        <div className="mt-1.5">{children}</div>
      </div>
    </div>
  );
}

function Meta({ label, valore }: { label: string; valore: string | null }) {
  if (!valore) return null;
  return (
    <div className="text-[11.5px] text-muted-foreground">
      <span className="font-semibold text-slate-600">{label}:</span> {valore}
    </div>
  );
}

function Allegati({
  slug,
  items,
  etichetta,
}: {
  slug: string;
  items: Allegato[];
  etichetta: string;
}) {
  if (!items.length) return null;
  return (
    <div className="mt-2.5">
      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        {etichetta}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((a) => (
          <AllegatoChip key={a.id} slug={slug} a={a} />
        ))}
      </div>
    </div>
  );
}
